import React, { useCallback, useMemo, useState } from 'react'
import { Modal } from 'shared'
import { connect } from 'formik'

import { CodonationSelectionProps, ModalContentProps, PredefinedAmountBoxProps } from './types'
import { FormValues } from 'donate/types'
import { formatCents, parseCents, sanitizeMoney } from '@betterplace/utils'

import { gaEvent } from 'shared'

const images = [
  'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-3-1e395bb471ac74d84b464698d679d7f786c51da262e3cb5ffb7cc2ada7c45193.png',
  'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-5-1ded43de393f6436c062886bf2ee7ae4e5b419286adb4de4d6157ff83aefa453.png',
  'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-7-7d86dc933b3fbfdbe829c3c1ea92033d201578227d412c9893f409755e6a5605.png',
] as const

export const CodonationSelection = ({ config, display }: CodonationSelectionProps): JSX.Element => {
  const [showCodonationModal, setShowCodonationModal] = useState(false)
  const openModal = useCallback(() => {
    gaEvent('engagement', 'click codonation_modal_open_link')
    setShowCodonationModal(true)
  }, [])
  const hideCodonationModal = useCallback(() => {
    setShowCodonationModal(false)
  }, [])

  const hideCodonationModalAndSendEvent = useCallback(() => {
    gaEvent('engagement', 'click codonation_modal_close_link')
    hideCodonationModal()
  }, [hideCodonationModal])

  return (
    <div className="donations-form-codonation-selection" style={{ textAlign: 'left' }}>
      <button className="simulated-link text-base whitespace-normal text-left" type="button" onClick={openModal}>
        {i18n.t('donate.codonation_selection.codonation_modal.open_button')}
      </button>

      <Modal
        id="codonation-selection-modal"
        onHide={hideCodonationModalAndSendEvent}
        show={showCodonationModal}
        header={i18n.t(`donate.codonation_selection.codonation_modal.${config.wording}.headline`, {
          receiver_name: display.receiverName,
        })}
        size="modal-lg"
      >
        <ModalContent config={config} display={display} handleClose={hideCodonationModal} />
      </Modal>
    </div>
  )
}

const ModalContent = connect<ModalContentProps, FormValues>(({ formik, config, display, handleClose }) => {
  const calc = formik.values._codonation_calculator
  const prefills = useMemo(() => calc.prefills(formik.values.amount_cents), [calc, formik.values.amount_cents])
  const [value, setValue] = useState(`${formik.values.codonation_cents / 100}`)

  const storeAndClose = useCallback(
    (cents) => {
      void formik.setFieldValue('codonation_cents', cents)
      formik.setFieldTouched('codonation_cents', true)
      handleClose()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleClose, formik.setFieldValue, formik.setFieldTouched]
  )

  const handlePredefinedAmountSelection = useCallback(
    (cents, boxPosition) => {
      gaEvent('engagement', 'click codonation_amount_box', boxPosition)
      storeAndClose(cents)
    },
    [storeAndClose]
  )

  const handleApplyButtonClick = useCallback(() => {
    if (calc.suggest(formik.values.amount_cents) !== parseCents(value)) {
      gaEvent('engagement', 'change codonation_amount_input', sanitizeMoney(value))
    }
    storeAndClose(parseCents(value))
  }, [storeAndClose, value, formik.values.amount_cents, calc])

  const handleEnter: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key == 'Enter') {
        storeAndClose(parseCents((event.target as HTMLInputElement).value))
      }
    },
    [storeAndClose]
  )
  const onValueChanged: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target }) => setValue(target.value),
    []
  )

  return (
    <div className="donations-form-codonation-selection">
      <p>{i18n.t(`donate.codonation_selection.codonation_modal.${config.wording}.platform_introduction`)}</p>

      <h3 className="text-lg">
        <span>{i18n.t(`donate.codonation_selection.codonation_modal.${config.wording}.call_to_action`)}</span>
        <span>:</span>
      </h3>

      <div className="flex mt-3 mb-3">
        <PredefinedAmountBox
          cents={prefills[0]}
          imageSrc={images[2]}
          boxPosition={1}
          onClick={handlePredefinedAmountSelection}
        />
        <PredefinedAmountBox
          cents={prefills[1]}
          imageSrc={images[1]}
          boxPosition={2}
          onClick={handlePredefinedAmountSelection}
        />
        <PredefinedAmountBox
          cents={prefills[2]}
          imageSrc={images[0]}
          boxPosition={3}
          onClick={handlePredefinedAmountSelection}
        />
      </div>

      <div className="custom-codonation-input-wrapper">
        <label>{i18n.t('donate.codonation_selection.codonation_modal.amount_label')}</label>
        <div className="input-group">
          <input
            className="form-control"
            type="text"
            inputMode="decimal"
            value={value}
            onChange={onValueChanged}
            onKeyPress={handleEnter}
          />
          <div className="input-group-append">
            <span className="input-group-text">€</span>
          </div>
        </div>
        <button className="btn btn-primary" type="button" onClick={handleApplyButtonClick}>
          {i18n.t('donate.codonation_selection.codonation_modal.change_amount_button')}
        </button>
      </div>

      {display.carrierName && (
        <div className="row">
          <div className="col-md-24">
            <div className="organisation">
              <img src={display.carrierProfilePicture} alt="" />
              <p>
                {i18n.t(`donate.codonation_selection.codonation_modal.${config.wording}.trust_teaser`, {
                  carrier_name: display.carrierName,
                })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

const PredefinedAmountBox = ({
  cents,
  imageSrc,
  boxPosition,
  onClick: onClick_,
}: PredefinedAmountBoxProps): JSX.Element => {
  const onClick = () => onClick_(cents, boxPosition)
  return (
    <button type="button" className="predefined-amount-box has-show-buttons-hovered" onClick={onClick}>
      {formatCents(i18n.locale, cents)}
      <div className="image-wrapper">
        <img src={imageSrc} alt="" />
      </div>
      <span className="btn btn-primary">{i18n.t('donate.codonation_selection.codonation_modal.choose_button')}</span>
    </button>
  )
}
